import type { CodeTriggerT, TimelineI, TimelineLabelI } from "tweenly"
import { ActionCreators } from "redux-undo"
import { setIsTimelineSettingChanged } from "../store/slices/editor.slice"
import { updateMasterTimeline } from "../store/slices/masterTimeline.slice"
import { updTimelineAction, uplTimelineAction } from "../store/slices/timeline.slice"
import { useAppDispatch, useAppStore } from "./useRedux"

export const useTimelineActions = () => {
    const dispatch = useAppDispatch()

    // Nothing here is subscribed. This hook is instantiated in every TimelineRow, every
    // TimeStamp and — through `useObjectActions` — in every CanvasItem and AbstractContainer,
    // and every value below is only needed while a handler runs. Components that RENDER a
    // duration use `useMasterTimeline`, components that render labels `useTimelineLabels`.
    const store = useAppStore()
    const getTimeline = (): TimelineI => store.getState().timeline.value
    const getTimeToSeek = () => store.getState().masterTimeline.time
    // `updateMasterTimeline` stores a NEW GSAPTimeline on every rebuild — i.e. at the end of
    // every drag — so subscribing to it here re-rendered the whole canvas each time
    const getMasterTimeline = (): GSAPTimeline | null => store.getState().masterTimeline.value

    const uploadTimeline = async (timeline: TimelineI, isImport?: boolean) => {
        getMasterTimeline()?.clear()
        await dispatch(uplTimelineAction({ timeline: timeline }))
        // commented to temporarily fix creation of gs-dev-tools timeline when app crashes
        dispatch(updateMasterTimeline(getTimeToSeek()))
        dispatch(ActionCreators.clearHistory())
    }

    const updateTimeline = (property: any, value: any) => {
        //[NOTE]: Timeline slice values exist only in the slice,
        // so there is no need to check if the values are the same because we cannot compare objects like TimelineLabelI and CodeTriggerT
        getMasterTimeline()?.clear()
        dispatch(setIsTimelineSettingChanged(true))
        dispatch(updTimelineAction({ property: property, value: value }))
        // @ts-ignore
        dispatch(updateMasterTimeline(getTimeToSeek()))
    }

    const addTimelineLabel = (label: TimelineLabelI) => {
        const timeline = getTimeline()
        if (timeline.labels?.find((lbl: TimelineLabelI) => lbl.time === label.time) !== undefined) {
            return
        }
        const newlabels: TimelineLabelI[] = timeline.labels ? [...timeline.labels, label] : [label]
        updateTimeline("labels", newlabels)
    }

    const delTimelineLabel = (labeltitle: string) => {
        updateTimeline(
            "labels",
            getTimeline().labels.filter((label: TimelineLabelI) => label.title !== labeltitle)
        )
    }

    const updTimelineLabelCode = (labeltitle: string, code: CodeTriggerT) => {
        updateTimeline(
            "labels",
            getTimeline().labels.map((label: TimelineLabelI) => {
                return label.title === labeltitle ? { ...label, onLabelCode: code } : label
            })
        )
    }

    const upsertTimelineLabel = (newLabel: TimelineLabelI) => {
        const timeline = getTimeline()
        if (timeline.labels.some((label) => label.title === newLabel.title)) {
            updateTimeline(
                "labels",
                timeline.labels.map((label: TimelineLabelI) => {
                    return label.title === newLabel.title ? newLabel : label
                })
            )
        } else {
            const newlabels: TimelineLabelI[] = [...timeline.labels, newLabel]
            updateTimeline("labels", newlabels)
        }
    }

    const refreshMasterTimeline = () => {
        // @ts-ignore
        getMasterTimeline()?.clear()
        dispatch(updateMasterTimeline(getTimeToSeek()))
    }

    const reinitTimeline = () => {
        // @ts-ignore
        getMasterTimeline()?.clear()
        dispatch(updateMasterTimeline(getTimeToSeek()))
    }

    const seekMasterTimeline = (position: string | number) => {
        getMasterTimeline()?.seek(position, false)
    }

    const pauseMasterTimeline = (position: string | number) => {
        getMasterTimeline()?.pause(position, false)
    }

    const jumpLabelInArray = (labels: TimelineLabelI[]): boolean => {
        return labels.find((label) => label.type === "jump") !== undefined
    }

    const pauseLabelInArray = (labels: TimelineLabelI[]): boolean => {
        return labels.find((label) => label.type === "pause") !== undefined
    }

    const setIntroEnd = (time: number | null): boolean => {
        const { outroStart } = getTimeline()
        if (time && outroStart && time >= outroStart) {
            return false
        }
        getMasterTimeline()?.clear()
        dispatch(setIsTimelineSettingChanged(true))
        dispatch(updTimelineAction({ property: "introEnd", value: time }))
        dispatch(updateMasterTimeline(getTimeToSeek()))
        return true
    }

    const setOutroStart = (time: number | null): boolean => {
        const { introEnd } = getTimeline()
        if (time && introEnd && time <= introEnd) {
            return false
        }
        getMasterTimeline()?.clear()
        dispatch(setIsTimelineSettingChanged(true))
        dispatch(updTimelineAction({ property: "outroStart", value: time }))
        dispatch(updateMasterTimeline(getTimeToSeek()))
        return true
    }

    const clearIntroEnd = (): boolean => setIntroEnd(null)

    const clearOutroStart = (): boolean => setOutroStart(null)

    return {
        uploadTimeline,
        updateTimeline,
        addTimelineLabel,
        delTimelineLabel,
        upsertTimelineLabel,
        updTimelineLabelCode,
        refreshMasterTimeline,
        reinitTimeline,
        seekMasterTimeline,
        pauseMasterTimeline,
        jumpLabelInArray,
        pauseLabelInArray,
        setIntroEnd,
        setOutroStart,
        clearIntroEnd,
        clearOutroStart,
    }
}
